import React, {useState, useEffect, useMemo} from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, LabelList, ReferenceLine, ComposedChart } from 'recharts';
import { PieChart, Pie, Cell, Legend, Treemap, Sankey, Sector, Label } from 'recharts';
import { GoArrowUpRight } from "react-icons/go";
import DemoSankeyLink from './DemoSankeyLink';
import DemoSankeyNode from './DemoSankeyNode';
import { PiMedalMilitaryFill } from 'react-icons/pi';
import { IoMdPerson } from "react-icons/io";
import { Download, Info } from 'lucide-react';

const isTimeMetric = (metricId) => ['aht', 'frt', 'frt_business_hours', 'rt_system', 'rt_agent', 'rt', 'total_talk_time'].includes(metricId);
const formatCompactTime = (seconds) => {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds)) return '';
  const totalMinutes = Math.floor(totalSeconds / 60);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

// Reusable PerformerCard Component
const PerformerCard = ({ title, performers, colorScheme, height="40vh", width="100%", handleDownload }) => {
  if(!performers){
    return;
  }
  // Check if data is parameter type (doesn't have calls)
  const isParameter = performers.length > 0;
  // Define colors based on scheme
  const colors = {
    top: {
      icon: 'text-green-500',
      background: 'bg-green-100',
      progressFill: 'bg-green-500',
      progressEmpty: 'bg-green-100',
      starColor: 'text-yellow-400'
    },
    bottom: {
      icon: 'text-red-500',
      background: 'bg-red-100',
      progressFill: 'bg-red-500',
      progressEmpty: 'bg-red-100',
      starColor: 'text-red-400'
    }
  };
  
  const scheme = colors[colorScheme];
  
  return (
    <div className="border rounded-lg p-4 shadow-sm w-full bg-white" style={{height: height, width: width}}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">{title}</h2>
        {typeof handleDownload === 'function' && (
          <Download className="text-gray-500 hover:text-gray-700 cursor-pointer" size={18} onClick={() => handleDownload(performers, title)} />
        )}
      </div>
      
      <div className="space-y-4 overflow-auto">
        {performers.map((performer) => (
            <div key={performer?.name} className="mb-6">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center">
                  <div className={`${scheme.background} p-2 rounded-full mr-3 w-10 h-10 flex items-center justify-center font-bold ${scheme.icon}`}>
                    {title.includes('Parameters') ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 ${scheme.icon}`}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 ${scheme.icon}`}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-lg">{performer?.name}</div>
                    <div className="text-gray-500">
                      {isParameter ? '' : `${performer?.calls} calls`}
                    </div>
                  </div>
                </div>
                <span className="font-bold text-3xl">{performer?.score}</span>
              </div>
              <div className="mt-1 pl-12">
                <div className="w-full h-2 rounded-full overflow-hidden bg-gray-200">
                  <div 
                    className={`h-full ${scheme.progressFill}`} 
                    style={{ width: `${performer?.score}%` }}
                  />
                </div>
              </div>
            </div>
        ))}
      </div>
    </div>
  );
};

// Reusable MetricTabs Component
const MetricTabs = ({ metrics, activeMetric, setActiveMetric, setShowDetails, font_size='text-3xl' }) => {
    return (
      <div className="flex flex-nowrap overflow-x-auto gap-4 mb-2">
        {metrics?.map((metric) => (
          <div 
            key={metric.id}
            onClick={() => setActiveMetric(metric.id)}
            className={`flex-none px-4 py-4 cursor-pointer transition-all duration-200 relative
              ${activeMetric === metric.id ? 'font-medium' : 'text-gray-500 hover:text-gray-700'}`} style={{padding: "1rem 0.7rem"}}
          >
            {activeMetric === metric.id && !metric.hideBorder && (
              <div 
                className="absolute top-0 left-0 right-0 h-1 rounded-t"
                style={{ backgroundColor: metric.color }}
              />
            )}
            
            <div className="text-sm font-medium" style={{display: "flex", minWidth: setShowDetails && metric.allowVisit ?  "9rem" : "5rem", justifyContent: "space-between", alignItems: "center"}}>{metric.title} 
              {setShowDetails && metric.allowVisit && <GoArrowUpRight className="cursor-pointer" onClick={event => { event.stopPropagation(); setShowDetails(metric); }} style={{color: "#3c09ffb5", fontSize: "20px"}}/>}</div>
            <div className={`${font_size} font-bold text-gray-800 mt-1`} style={{textAlign: "center"}}>
              {typeof metric.formatter === 'function' 
                ? metric.formatter(metric.value) 
                : metric.value}
            </div>
            <div className={`text-sm font-medium mt-1 ${parseFloat(metric.change) >= 0 ? 'text-green-500' : 'text-red-500'}`} style={{textAlign: "center"}}>
              {parseFloat(metric.change) >= 0 ? '+' : ''}{metric.change}% 
              {parseFloat(metric.change) >= 0 ? ' ↑' : ' ↓'}
            </div>
          </div>
        ))}
      </div>
    );
  };

const NumberCard = ({metric, activeMetric, setActiveMetric, setShowDetails, font_size='text-3xl', height, width}) => {
  if(!metric){
    return;
  }
  return (
    <div className="flex flex-nowrap overflow-x-auto gap-4 mb-2" style={{height: "100%", justifyContent: "center"}}>
        <div 
          key={metric?.id}
          onClick={() => setActiveMetric(metric?.id)}
          className={`flex-none px-4 py-4 cursor-pointer transition-all duration-200 relative
            ${activeMetric === metric?.id ? 'font-medium' : 'text-gray-500 hover:text-gray-700'}`} style={{padding: "1rem 0.7rem", width: width}}
        >
          {activeMetric === metric?.id && !metric.hideBorder && (
            <div 
              className="absolute top-0 left-0 right-0 h-1 rounded-t"
              style={{ backgroundColor: metric.color }}
            />
          )}
          
          <div className="text-sm font-medium" style={{display: "flex", minWidth: setShowDetails && metric?.allowVisit ?  "9rem" : "5rem", justifyContent: "space-between"}}><span style={{flex: "0.33", marginRight: "5px"}}></span><span style={{textAlign: "center"}}>{metric?.title}</span> 
            <span style={{marginLeft: "5px", flex: "0.33", display: "flex", justifyContent: "end"}}>{setShowDetails && metric?.allowVisit && <GoArrowUpRight onClick={()=>setShowDetails(metric)} style={{color: "#3c09ffb5", fontSize: "20px"}}/>}</span></div>
          <div className={`${font_size} font-bold text-gray-800 mt-1`} style={{textAlign: "center"}}>
            {metric?.value && !isNaN(metric?.value) ? metric.value : (metric.value !== null && metric.value !== '') ? '0' : ''}
          </div>
          {metric?.change && <div className={`text-sm font-medium mt-1 ${parseFloat(metric?.change) >= 0 ? 'text-green-500' : 'text-red-500'}`} style={{textAlign: "center"}}>
            {parseFloat(metric?.change) >= 0 ? '+' : ''}{metric?.change}% 
            {parseFloat(metric?.change) >= 0 ? ' ↑' : ' ↓'}
          </div>}
          {/* {!metric?.change && <div className={`text-sm font-medium mt-1 text-gray-500`} style={{textAlign: "center"}}>
            0% -
          </div>} */}
        </div>
    </div>
  );
}

// Reusable TrendsLineWithMetrics Component
const TrendsLineWithMetrics = ({ 
  data, 
  metrics, 
  activeMetric, 
  setActiveMetric, 
  formatDate, 
  formatTime, 
  title, 
  width = "w-full md:w-3/5", 
  calculatePercentChange, setShowDetails,
  useMetricTabs = true,
  defaultSmoothing = '7', // '1' | '3' | '7'
  smoothingWindow, // optional controlled prop
  yDomain, // optional domain override, e.g., [0, 100]
  handleDownload,
  infoText
}) => {
  const [smoothing, setSmoothing] = useState(smoothingWindow || defaultSmoothing); // '1' | '3' | '7'
  useEffect(() => {
    if (smoothingWindow) setSmoothing(smoothingWindow);
  }, [smoothingWindow]);

  const displayData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return data;
    const windowSize = Number(smoothing || '7'); // 1, 3, 7
    // 1D = no smoothing (original granularity)
    if (windowSize <= 1) return data;
    // Aggregate into non-overlapping windows of size N to reduce points
    const out = [];
    for (let i = 0; i < data.length; i += windowSize) {
      const slice = data.slice(i, i + windowSize);
      let sum = 0;
      let count = 0;
      for (let k = 0; k < slice.length; k++) {
        const raw = slice[k]?.[activeMetric];
        const num = typeof raw === 'number' ? raw : parseFloat(raw);
        if (Number.isFinite(num)) { sum += num; count += 1; }
      }
      const avg = count > 0 ? sum / count : null;
      const last = slice[slice.length - 1] || data[i];
      out.push({
        ...last,
        [activeMetric]: avg == null ? null : Math.round(avg * 100) / 100
      });
    }
    return out;
  }, [data, activeMetric, smoothing]);
  const prepareMetricSheets = () => (
    (metrics || []).map((metric) => ({
      name: metric.title || metric.id,
      rows: (data || []).map((row) => ({
        date: row.date,
        metric: metric.title || metric.id,
        value: row[metric.id],
      })),
    }))
  );

  return (
    <div className={`${width} bg-white rounded-lg p-4`} style={{height: "100%"}}>
      <div className="flex items-center justify-between">

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-semibold text-gray-700 flex items-center">
          {title}
          {infoText && (
            <div className="ml-2 relative group">
              <Info className="h-4 w-4 text-gray-500 cursor-help" />
              <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-sm text-gray-700 text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                {infoText}
              </div>
            </div>
          )}
        </h3>
      </div>
      <div className="flex items-center gap-3">
      {typeof handleDownload === 'function' && (
        <Download className="text-gray-500 hover:text-gray-700 cursor-pointer" size={18} onClick={() => handleDownload(prepareMetricSheets(), title, { workbook: true })} />
      )}
      <div className="mb-3">
        <div className="flex items-center flex-col gap-2">
          {/* <div className="text-xs text-gray-400">Moving Averages</div> */}
          <div className="flex items-center gap-2">
          {['1','3','7'].map(win => (
            <button
              key={win}
              onClick={()=>setSmoothing(win)}
              className={`px-2 py-1 text-xs rounded border ${smoothing===win ? 'bg-gray-500 text-white border-gray-500' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
            >
              {win}D
            </button>
          ))}
          </div>
        </div>
      </div>
      </div>
      </div>
      
      {useMetricTabs ? (
        <MetricTabs 
          metrics={metrics} 
          activeMetric={activeMetric} 
          setActiveMetric={setActiveMetric} setShowDetails={setShowDetails}
        />
      ) : (
        <div className="flex flex-nowrap overflow-x-auto gap-4 mb-2">
          {metrics?.map((metric) => (
            <div 
              key={metric.id}
              onClick={() => setActiveMetric(metric.id)}
              className={`flex-none px-6 py-4 cursor-pointer transition-all duration-200 relative
                ${activeMetric === metric.id ? 'font-medium' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {activeMetric === metric.id && (
                <div 
                  className="absolute top-0 left-0 right-0 h-1 rounded-t"
                  style={{ backgroundColor: metric.color }}
                />
              )}
              
              <div className="text-sm font-medium">{metric.title}</div>
              <div className="text-3xl font-bold text-gray-800 mt-1">
                {data[data.length - 1][metric.id]}
              </div>
              <div className={`text-sm font-medium mt-1 ${parseFloat(calculatePercentChange(metric.id)) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {parseFloat(calculatePercentChange(metric.id)) >= 0 ? '+' : ''}{calculatePercentChange(metric.id)}% 
                {parseFloat(calculatePercentChange(metric.id)) >= 0 ? ' ↑' : ' ↓'}
              </div>
            </div>
          ))}
        </div>
      )}

      <hr className="border-gray-200 mb-0" />

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={displayData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate} 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
            />
            <YAxis 
              domain={yDomain || ['auto', 'auto']}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
              tickFormatter={(value) => {
                if (isTimeMetric(activeMetric) && formatTime) {
                  return formatCompactTime(value);
                }
                return value;
              }}
              width={72}
            />
            <Tooltip 
              formatter={(value, name) => {
                const metric = metrics?.find(m => m.id === activeMetric);
                if (isTimeMetric(metric?.id) && formatTime) {
                  return [formatTime(value), metric.title];
                }
                return [value, metric?.title || name];
              }}
              labelFormatter={(label) => formatDate(label)}
              contentStyle={{ borderRadius: '6px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            />
            <Line 
              type="monotoneX" 
              dataKey={activeMetric} 
              stroke={metrics?.find(m => m.id === activeMetric)?.color} 
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6, strokeWidth: 0 }}
              strokeLinecap="round"
              strokeLinejoin="round"
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const VerticalBarGraph = ({ barData, title, width = "w-full md:w-1/2", targetValue = 8, maxValue = 10, handleDownload }) => {
    const numericTarget = Number(targetValue);
  
    const processedData = barData.map(item => ({
      ...item,
      value: item.value !== null && item.value !== '' ? Number(item.value) : ''
    }));
  
    const defaultColors = ["rgb(79, 70, 229)"];
  
    return (
      <div className={`${width} bg-white rounded-lg`} style={{ height: "60vh", overflow: "auto", padding: "1rem" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-700">{title}</h3>
          {typeof handleDownload === 'function' && (
            <Download className="text-gray-500 hover:text-gray-700 cursor-pointer" size={18} onClick={() => handleDownload(processedData, title)} />
          )}
        </div>
        <div style={{ width: "120%", marginLeft: "-8rem", marginTop: "2rem", overflow: 'auto', height: "100%"}}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={processedData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis 
                type="number" 
                domain={[0, maxValue]} 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
              />
              <YAxis 
                type="category" 
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#4B5563', fontSize: 12 }}
                width={110}
              />
              <Tooltip
                formatter={(value) => [value, 'Score']}
                contentStyle={{ borderRadius: '6px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
              />
              {/* <ReferenceLine 
                x={numericTarget} 
                stroke="#000000" 
                strokeWidth={2}
                strokeDasharray="5 3" 
                label={{ 
                  value: 'TARGET', 
                  position: 'top', 
                  fill: '#000000',
                  fontSize: 14,
                  fontWeight: 'bold'
                }} 
              /> */}
              <Bar 
                dataKey="value" 
                background={{ fill: '#f5f5f5' }}
                radius={[0, 4, 4, 0]}
              >
                {processedData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.fill ? entry.fill : defaultColors[index % defaultColors.length]} 
                  />
                ))}
                <LabelList dataKey="value" position="right" fill="#4B5563" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };
  
// Horizontal Bar Chart Component for Performance Metrics
const HorizontalBarMetrics = ({ 
  data, 
  metrics, 
  activeMetric, 
  setActiveMetric, 
  formatTime, 
  title, 
  setShowDetails,
  calculatePercentChange,
  handleDownload
}) => {
  if(!data || !metrics){
    return;
  }
  // Transform data for bar chart comparison
  const transformedData = data.map(agent => ({
    name: agent.name,
    value: agent[activeMetric],
    fill: agent.fill || metrics.find(m => m.id === activeMetric)?.color || '#4F46E5'
  }));

  // Sort data from highest to lowest
  const sortedData = [...transformedData].sort((a, b) => b.value - a.value);
  const prepareMetricSheets = () => (
    (metrics || []).map((metric) => ({
      name: metric.title || metric.id,
      rows: (data || []).map((row) => ({
        name: row.name,
        metric: metric.title || metric.id,
        value: row[metric.id],
      })),
    }))
  );
  const CustomizedYAxisTick = ({ x, y, payload }) => {
    const maxCharsPerLine = 13;
    const value = payload.value;
  
    const lines = [];
    for (let i = 0; i < value.length; i += maxCharsPerLine) {
      lines.push(value.substring(i, i + maxCharsPerLine));
    }
  
    return (
      <text
        x={x}
        y={y}
        textAnchor="end"
        fill="#4B5563"
        fontSize={12}
        fontFamily="sans-serif"
      >
        {lines.map((line, index) => (
          <tspan x={x} dy={index === 0 ? 0 : 14} key={index}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };  

  return (
    <div className="w-full bg-white rounded-lg shadow-sm" style={{height: "100%"}}>
      <div className="flex items-center justify-between p-4">
        <h3 className="text-xl font-semibold text-gray-700">{title}</h3>
        {typeof handleDownload === 'function' && (
          <Download className="text-gray-500 hover:text-gray-700 cursor-pointer" size={18} onClick={() => handleDownload(prepareMetricSheets(), title, { workbook: true })} />
        )}
      </div>
      
      <MetricTabs 
        metrics={metrics} 
        activeMetric={activeMetric} 
        setActiveMetric={setActiveMetric} setShowDetails={setShowDetails}
      />

      <hr className="border-gray-200 mb-0" />

      <div className="p-4 overflow-y-auto" style={{height: "57%"}}>
        <div className="h-full min-h-108" style={{ minHeight: `${Math.max(400, sortedData.length * 60)}px`, marginLeft: "-8rem" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sortedData}
              layout="vertical"
              margin={{ top: 20, right: 50, left: 120, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis 
                type="number" 
                domain={['dataMin - 1', 'dataMax + 9']} 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={(value) => {
                  if (isTimeMetric(activeMetric) && formatTime) {
                    return formatCompactTime(value);
                  }
                  return value;
                }}
              />
              <YAxis 
                type="category" 
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTick />}
                width={110}
                interval={0}
              />
              <Tooltip 
                formatter={(value, name) => {
                  if (isTimeMetric(activeMetric) && formatTime) {
                    return [formatTime(value), metrics.find(m => m.id === activeMetric)?.title || 'Value'];
                  }
                  return [value, metrics.find(m => m.id === activeMetric)?.title || 'Value'];
                }}
                contentStyle={{ borderRadius: '6px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
              />
              <Bar 
                dataKey="value" 
                background={{ fill: '#f5f5f5' }}
                radius={[0, 4, 4, 0]}
                label={{
                  position: 'right',
                  formatter: (value) => isTimeMetric(activeMetric) && formatTime ? formatCompactTime(value) : value,
                  fill: '#4B5563',
                  fontSize: 12
                }}
              >
                {sortedData.map((entry, index) => {

                  
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.fill || '#4F46E5'}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const CompetencyAssessment = ({ title, competencyData, competencyMetrics, handleDownload }) => {
  // Always call hooks at the top
  const defaultCompetency = (competencyMetrics && competencyMetrics.length > 0) ? competencyMetrics[0].id : null;
  const [activeCompetency, setActiveCompetency] = useState(defaultCompetency);

  // Ensure default selection updates when competencyMetrics load/change
  useEffect(() => {
    if (Array.isArray(competencyMetrics) && competencyMetrics.length > 0) {
      const hasActive = activeCompetency && competencyMetrics.some(m => m.id === activeCompetency || m.title === activeCompetency || m.name === activeCompetency);
      if (!hasActive) {
        setActiveCompetency(competencyMetrics[0].id);
      }
    }
  }, [competencyMetrics]);

  // Early return if data is missing
  if(!competencyData || !competencyMetrics || !competencyMetrics.length){
    return null;
  }

  // Transform data for the chart
  const transformedData = competencyData.map(agent => ({
    name: agent.name,
    value: agent?.[activeCompetency] ? Math.round(agent?.[activeCompetency], 0) : '',
    fill: competencyMetrics.find(m => m.id === activeCompetency)?.color || '#4F46E5'
  }));

  // Sort data from highest to lowest
  const sortedData = [...transformedData].sort((a, b) => (b.value !== '' ? b.value - a.value : -1));
  const prepareMetricSheets = () => (
    (competencyMetrics || []).map((metric) => ({
      name: metric.title || metric.id,
      rows: (competencyData || []).map((row) => ({
        name: row.name,
        metric: metric.title || metric.id,
        value: row[metric.id],
      })),
    }))
  );

  return (
    <div className="w-full">
      <div className="flex items-center justify-between p-4">
        <h3 className="text-xl font-semibold text-gray-700">{title}</h3>
        {typeof handleDownload === 'function' && (
          <Download className="text-gray-500 hover:text-gray-700 cursor-pointer" size={18} onClick={() => handleDownload(prepareMetricSheets(), title, { workbook: true })} />
        )}
      </div>
      {/* Metric Tabs */}
      <div className="flex flex-nowrap overflow-x-auto gap-4 mb-2">
        {competencyMetrics.map((metric) => (
          <div 
            key={metric.id}
            onClick={() => setActiveCompetency(metric.id)}
            className={`flex-none px-6 py-4 cursor-pointer transition-all duration-200 relative
              ${activeCompetency === metric.id ? 'font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {activeCompetency === metric.id && (
              <div 
                className="absolute top-0 left-0 right-0 h-1 rounded-t"
                style={{ backgroundColor: metric.color }}
              />
            )}
            <div className="text-sm font-medium">{metric.title}</div>
            <div className="text-3xl font-bold text-gray-800 mt-1">
              {/* Show the value for this metric, not just the active one */}
              {/* {metric.value} */}
              {metric.value!=null && metric.value !== '' ? Math.round(metric.value, 0) : ''}
            </div>
            <div className={`text-sm font-medium mt-1 ${parseFloat(metric.change) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {parseFloat(metric.change) >= 0 ? '+' : ''}{metric.change}% 
              {parseFloat(metric.change) >= 0 ? ' ↑' : ' ↓'}
            </div>
          </div>
        ))}
      </div>
      <hr className="border-gray-200 mb-0" />
      {/* Bar Chart */}
      <div className="p-4 overflow-y-auto" style={{height: "27rem"}}>
        <div className="h-full min-h-84" style={{ minHeight: `${Math.max(400, sortedData.length * 60)}px`, marginLeft: "-8rem" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sortedData}
              layout="vertical"
              margin={{ top: 20, right: 50, left: 120, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis 
                type="number" 
                domain={[0, 100]} // Competency scores out of 100
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
              />
              <YAxis 
                type="category" 
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#4B5563', fontSize: 12 }}
                width={110}
                interval={0}
              />
              <Tooltip 
                formatter={(value) => [value !== '' ? value.toFixed(0) : '', competencyMetrics.find(m => m.id === activeCompetency)?.title || 'Competency']}
                contentStyle={{ borderRadius: '6px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
              />
              {/* {targetValue && (
                <ReferenceLine 
                  x={targetValue} 
                  stroke="#000000" 
                  strokeWidth={2}
                  strokeDasharray="5 3" 
                  label={{ 
                    value: `TARGET: ${targetValue.toFixed(1)}`, 
                    position: 'top', 
                    fill: '#000000',
                    fontSize: 14,
                    fontWeight: 'bold'
                  }} 
                />
              )} */}
              <Bar 
                dataKey="value" 
                background={{ fill: '#f5f5f5' }}
                radius={[0, 4, 4, 0]}
                label={{
                  position: 'right',
                  formatter: (value) => value !== '' ? value.toFixed(0) : ''
                }}
              >
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// Custom Treemap label renderer
const CustomizedContent = (props) => {
  const { root, depth, x, y, width, height, index, colors, name, value } = props;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: depth < 2 ? colors[Math.floor((index / root.children.length) * 6)] : '#ffffff',
          stroke: '#fff',
          strokeWidth: 2 / (depth + 1e-10),
          strokeOpacity: 1 / (depth + 1e-10),
        }}
      />
      {width > 50 && height > 30 && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#fff"
          fontSize={12}
        >
          {name}
        </text>
      )}
      {width > 50 && height > 50 && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 12}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#fff"
          fontSize={10}
        >
          {value}
        </text>
      )}
    </g>
  );
};

const TreeMap = ({title, productCategoryData}) => {
  <div className="mb-10">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium mb-2">{title}</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <Treemap
                data={productCategoryData}
                dataKey="size"
                ratio={4/3}
                stroke="#fff"
                fill="#8884d8"
                content={<CustomizedContent colors={["#8884d8", "#83a6ed", "#8dd1e1", "#82ca9d", "#a4de6c", "#d0ed57", "#ffc658"]} />}
              >
                <Tooltip formatter={(value) => [`${value} units`, "Size"]} />
              </Treemap>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
}

const DynamicSankeyChart = ({data, title, onClickNode, handleDownload}) => {
  if(!data){
    return;
  }
  
  // Simple approach: Sum only the values from root nodes (nodes with no incoming edges)
  const calculateCorrectTotal = () => {
    if (!data.links) return 0;
    
    // Find nodes that have incoming edges (are targets)
    const targetNodes = new Set(data.links.map(link => link.target));
    
    // Sum values from links that start from root nodes (not targets of any other link)
    const rootLinkValues = data.links
      .filter(link => !targetNodes.has(link.source))
      .reduce((sum, link) => sum + link.value, 0);
    
    return rootLinkValues;
  };
  
  const totalValue = calculateCorrectTotal();
  
  // Calculate dynamic width based on data complexity
  const calculateChartDimensions = () => {
    if (!data.nodes) return { width: 1200, height: 800 };
    
    const nodeCount = data.nodes.length;
    const maxNameLength = Math.max(...data.nodes.map(node => node.name.length));
    
    // Calculate minimum width needed for 3-level layout with better spacing
    const minWidthPerLevel = 500; // Increased space per level
    const baseWidth = 1400; // Increased base width significantly
    const calculatedWidth = Math.max(baseWidth, minWidthPerLevel * 3);
    
    // Calculate height based on node count with more generous spacing
    const minHeight = 800; // Increased minimum height
    const calculatedHeight = Math.max(minHeight, nodeCount * 35); // More space per node
    
    return {
      width: Math.min(calculatedWidth, 1200), // Increased max width
      height: Math.min(calculatedHeight, 1400)   // Increased max height
    };
  };
  
  const { width: chartWidth, height: chartHeight } = calculateChartDimensions();
  
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
  
    // The data structure in the payload is different depending on what you're hovering over
    const data = payload[0].payload.payload;
    
    // Determine if we're looking at a link or node
    const isLink = data.source && data.target;
    const value = data.value;
    const percentage = ((value / totalValue) * 100).toFixed(1);
    
    // Determine the name to display
    let name;
    if (isLink) {
      name = `${data.target.name}`;
    } else {
      name = data.name || "Total";
    }
  
    return (
      <div className="bg-white shadow-lg rounded-md p-2 border border-gray-300 text-sm" style={{borderRadius: "8px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)"}}>
        <p className="text-sm">{name}: {value} <span style={{color:"gray"}}>({percentage}%)</span></p>
      </div>
    );
  };
  const prepareDownloadRows = () => {
    const nodes = data?.nodes || [];
    return (data?.links || []).map((link) => ({
      source: nodes[link.source]?.name ?? link.source,
      target: nodes[link.target]?.name ?? link.target,
      value: link.value,
    }));
  };

  return (
    <div style={{background: "white", borderRadius: "6px", padding: "10px"}}>
      <div className="flex items-center justify-between">
        {title && <h3 className="text-xl font-semibold text-gray-700 mb-4 p-4">{title}</h3>}
        {typeof handleDownload === 'function' && (
          <Download className="text-gray-500 hover:text-gray-700 cursor-pointer mr-3" size={18} onClick={() => handleDownload(prepareDownloadRows(), title || 'sankey')} />
        )}
      </div>
      <div 
        style={{
          width: "100%",
          height: "600px",
          overflow: "auto",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          padding: "15px" // Increased padding
        }}
      >
        <div style={{ width: chartWidth, height: chartHeight, minWidth: "1000px", minHeight: "600px" }}>
          <Sankey
            width={chartWidth} 
            height={chartHeight}
            margin={{ top: 40, bottom: 40, right: 340, left: 20 }}
            data={data}
            nodeWidth={15} 
            nodePadding={10}
            linkCurvature={0.4}
            iterations={0}
            isAnimationActive={false}
            link={<DemoSankeyLink />}
            node={<DemoSankeyNode containerWidth={chartWidth} totalValue={totalValue} onClickNode={onClickNode} />}
          >
            <defs>
              <linearGradient id={'linkGradient'}>
                <stop offset="0%" stopColor="#e8ddff" />
                <stop offset="100%" stopColor="#e8ddff" />
              </linearGradient>
            </defs>
            <Tooltip isAnimationActive={false} content={<CustomTooltip />} />
          </Sankey>
        </div>
      </div>
    </div>
  )
}

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, style } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        style={{ cursor: 'pointer', ...style }}
      />
    </g>
  );
};

const PieChart2 = ({
  pieData,
  colors,
  title,
  width = 'w-full max-w-3xl',
  onSegmentClick,
  showInfo = false,
  handleDownload,
  showHeader = true,
  valueFormatter = (value) => value,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const totalValue = Number(((pieData?.reduce((sum, entry) => sum + entry.value, 0)) ?? 0).toFixed(2));
  if(!pieData){
    return;
  }
  let pieDataCopy = [...pieData];
  pieDataCopy.sort((a, b) => b.value - a.value);

  // Helper to choose color by label for this chart only
  const getColorByLabel = (label, index) => {
    const entry = pieDataCopy.find((item) => item.name === label);
    if (entry?.fill) return entry.fill;
    if (Array.isArray(colors) && colors.length > 0 && typeof colors[0] === 'object') {
      const match = colors.find((c) => c.name === label);
      if (match?.color) return match.color;
    }
    if (!Array.isArray(colors) || colors.length === 0) return '#5086EC';
    return colors[index % colors.length];
  };

  const handleSliceClick = (data) => {
    if (onSegmentClick) onSegmentClick(data);
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
    setTooltipVisible(true);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
    setTooltipVisible(false);
  };

  const CustomTooltip = ({ active, payload, tooltipTitle }) => {
    if (active && payload && payload.length) {
      const hoveredEntry = payload[0];
      const hoveredName = hoveredEntry.name;
      const hoveredValue = hoveredEntry.value;
      const hoveredIndex = pieDataCopy.findIndex((item) => item.name === hoveredName);
      const hoveredPercentage = ((hoveredValue / totalValue) * 100).toFixed(2);
      const hoveredColor = getColorByLabel(hoveredName, hoveredIndex);
      const otherCategories = pieDataCopy.filter((e) => e.name !== hoveredName);

      return (
        <div className=" p-4 rounded shadow-lg" style={{ minWidth: '240px', maxHeight: '60vh', overflow: 'auto',backdropFilter: "blur(15px)", backgroundColor: "rgba(255, 255, 255, 0.1)" }}>
          {/* <h3 className="text-md font-bold mb-3 uppercase">{tooltipTitle}</h3> */}

          {/* Hovered category */}
          <div className="flex items-start" >
            <div className="w-4 h-4 rounded-full mr-3 mt-0.5" style={{ backgroundColor: hoveredColor }} />
            <div className="flex-1 text-sm break-words font-semibold leading-relaxed pr-2 min-w-0">{hoveredName}</div>
            <div className="flex items-center ml-auto flex-shrink-0">
              <span className="text-sm font-bold mr-3">{valueFormatter(hoveredValue)}</span>
              <span className="text-gray-700 text-sm">{hoveredPercentage} %</span>
            </div>
          </div>

          {/* <div className="border-t border-gray-600 my-2" /> */}

          {/* Other categories */}
          {/* {otherCategories.map((entry) => {
            const entryIndex = pieDataCopy.findIndex((item) => item.name === entry.name);
            const percentage = ((entry.value / totalValue) * 100).toFixed(2);
            const entryColor = getColorByLabel(entry.name, entryIndex);
            return (
              <div key={entry.name} className="flex items-start mb-2 opacity-85">
                <div className="w-4 h-4 rounded-full mr-3 mt-0.5" style={{ backgroundColor: entryColor }} />
                <div className="flex-1 text-sm break-words leading-relaxed pr-4 min-w-0">{entry.name}</div>
                <div className="flex items-center ml-auto flex-shrink-0">
                  <span className="text-sm mr-3">{entry.value}</span>
                  <span className="text-gray-400 text-sm">{percentage} %</span>
                </div>
              </div>
            );
          })} */}

          {/* <div className="border-t border-gray-600 my-2" /> */}

          {/* Total */}
          {/* <div className="flex items-center">
            <div className="w-4 mr-1" />
            <span className="text-sm">Total</span>
            <div className="ml-auto flex items-center">
              <span className="text-sm font-bold mr-3">{totalValue}</span>
              <span className="text-gray-400 text-sm">100 %</span>
            </div>
          </div> */}
        </div>
      );
    }
    return null;
  };

  const renderColorLegend = () => {
    const halfLength = Math.ceil(pieDataCopy.length / 2);
    return (
      <div className="flex flex-col space-y-2 cursor-pointer" style={{maxHeight: "20vh", overflow: "auto"}}>
        <div className="flex flex-wrap justify-center gap-2">
          {pieDataCopy.slice(0, halfLength).map((entry, idx) => (
            <div key={entry.name} className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: getColorByLabel(entry.name, idx) }} />
              <span className="text-gray-700">{entry.name} - {valueFormatter(entry.value)} ({((entry.value / totalValue) * 100).toFixed(2)}%)</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {pieDataCopy.slice(halfLength).map((entry, idx) => (
            <div key={entry.name} className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: getColorByLabel(entry.name, idx + halfLength) }}
              />
              <span className="text-gray-700">{entry.name} - {valueFormatter(entry.value)} ({((entry.value / totalValue) * 100).toFixed(2)}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`${width} mx-auto`} style={{ height: '100%' }}>
      <div className="bg-white rounded-lg p-3" style={{ height: '100%' }}>
        {showHeader && (
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
              {title}
              {showInfo && (
                <div className="ml-2 relative group">
                  <Info className="h-4 w-4 text-gray-500 cursor-help" />
                  <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-sm text-gray-700 text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                    Poor: 0-50, Average: 51-75, Good: 76-90, Excellent: 91-100
                  </div>
                </div>
              )}
            </h2>
            {typeof handleDownload === 'function' && (
              <div>
                <Download className="text-gray-500 hover:text-gray-700 cursor-pointer mr-3 mt-3 mb-3" size={20} onClick={()=>handleDownload(pieData, title)} />
              </div>
            )}
          </div>
        )}
        {!showHeader && <div className="h-1" />}
        <div className="h-60 mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart onMouseLeave={() => { setActiveIndex(null); setTooltipVisible(false); }}>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={pieDataCopy}
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={110}
                dataKey="value"
                onMouseEnter={onPieEnter}
                onMouseLeave={onPieLeave}
                paddingAngle={2}
                onClick={handleSliceClick}
              >
                {pieDataCopy.map((entry, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={getColorByLabel(entry.name, idx)}
                    style={{ cursor: 'pointer', pointerEvents: 'all', outline: 'none' }}
                  />
                ))}
              </Pie>
              <Tooltip
                isAnimationActive={false}
                content={<CustomTooltip tooltipTitle={title} />}
                wrapperStyle={{ pointerEvents: 'none' }}
              />
              <text
                x="50%"
                y="50%"
                dy={-10}
                textAnchor="middle"
                fill="#333"
                fontSize={24}
                fontWeight="bold"
                style={{ pointerEvents: 'none' }}
              >
                {valueFormatter(totalValue)}
              </text>
              <text
                x="50%"
                y="50%"
                dy={15}
                textAnchor="middle"
                fill="#666"
                fontSize={16}
                style={{ pointerEvents: 'none' }}
              >
                TOTAL
              </text>
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4">{renderColorLegend()}</div>
      </div>
    </div>
  );
};

const StackedBarChart = ({ 
  data = [],
  title = "Monthly Distribution",
  height = 400,
  // Define color sets - will be used based on category count
  colorSets = {
    primary: ["#5470c6", "#91cc75", "#fac858", "#ee6666", "#73c0de", "#3ba272", "#fc8452", "#9a60b4"],
    muted: ["#8096d3", "#b8dcac", "#fad88e", "#f9a6a6", "#a9d7e9", "#84c2a8", "#fdaf85", "#bfa0d2"],
    pastel: ["#bec8e4", "#d3e8cc", "#fce8c2", "#fac8c8", "#c8e6f0", "#b6ded0", "#fdd5ba", "#d9c8e1"],
    dark: ["#324195", "#4e8c31", "#d49d21", "#bc4040", "#3d849b", "#1d704c", "#d05f24", "#754696"]
  },
  // Default to a custom palette that's balanced between vibrant and muted
  barColors = ["#5086EC", "#91BFB2", "#F3CA8C", "#E6B8B8"],
  categories = ["Medium Potential", "Low Interest", "No Potential", "Unknown"],
  xAxisKey = "name",
  xAxisTitle = "Date",
  yAxisTitle = "Number of Calls",
  showLegend = true,
  showGrid = true,
  darkMode = false,
  colorVariant = "default", // Options: "default", "bold", "muted", "pastel", "dark"
  handleDownload, // optional: (rows, title) => void
}) => {
  const roundToTwoDecimals = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : value;
  };

  const totalValue = data?.reduce((sum, entry) => sum + entry.value, 0);
  
  // Custom legend that matches the dark theme
  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex flex-wrap justify-center gap-2 pt-6 pb-2" style={{height: "12vh", overflow: "auto"}}>
        {payload.map((entry, index) => (
          <div key={`legend-item-${index}`} className="flex items-center">
            <div 
              className="w-3 h-3 rounded-full mr-2" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };
  
  
  
  // Create a consistent color mapping that will be stable across data changes
  const getColorPalette = () => {
    if (colorVariant === "bold") return colorSets.primary;
    if (colorVariant === "muted") return colorSets.muted;
    if (colorVariant === "pastel") return colorSets.pastel;
    if (colorVariant === "dark") return colorSets.dark;
    return barColors; // default custom palette
  };

  const palette = getColorPalette();
  
  // Create a consistent color mapping for categories
  const categoryColorMap = {};
  categories.forEach((category, index) => {
    categoryColorMap[category] = palette[index % palette.length];
  });

  // Calculate total for each data point and overall total
  const totals = data.map(item => {
    const total = roundToTwoDecimals(
      categories.reduce((acc, category) => acc + (item[category] || 0), 0)
    );
    return { ...item, total };
  });

  // Calculate grand total for header display
  const grandTotal = roundToTwoDecimals(
    totals.reduce((acc, item) => acc + item.total, 0)
  );

  // Calculate category totals and percentages for the overall data
  const categoryTotals = categories.reduce((acc, category) => {
    const total = roundToTwoDecimals(
      data.reduce((sum, item) => sum + (item[category] || 0), 0)
    );
    acc[category] = total;
    return acc;
  }, {});

  const categoryPercentages = categories.reduce((acc, category) => {
    const percentage = ((categoryTotals[category] / grandTotal) * 100).toFixed(2);
    acc[category] = percentage;
    return acc;
  }, {});

  // Background and text colors based on mode
  const bgColor = darkMode ? "bg-gray-900" : "bg-white";
  const textColor = darkMode ? "text-white" : "text-gray-800";
  const subTextColor = darkMode ? "text-gray-400" : "text-gray-500";
  const axisColor = darkMode ? "#9CA3AF" : "#6B7280";
  const CustomTooltip = ({ active, payload, label, tooltipTitle }) => {
    if (active && payload && payload.length) {
      // For stacked bar charts, payload contains all segments for the hovered bar
      // We need to find which specific segment is being hovered
      const hoveredEntry = payload.find(entry => entry.dataKey && entry.value > 0) || payload[0];
      const hoveredName = hoveredEntry.dataKey; // Use dataKey for category name in stacked bars
      const hoveredValue = hoveredEntry.value;
      
      // Get the data point for this specific date/bar
      const dataPoint = hoveredEntry.payload;
      
      // Calculate total by summing all category values for this data point
      const totalValue = roundToTwoDecimals(
        categories.reduce((sum, category) => sum + (dataPoint[category] || 0), 0)
      );
      
      // Handle NaN percentage when totalValue is 0
      const hoveredPercentage = totalValue === 0 ? "0.00" : ((hoveredValue / totalValue) * 100).toFixed(2);
      const hoveredColor = categoryColorMap[hoveredName];
      
      // Get other categories for this data point, excluding the hovered one
      const otherCategories = categories
        .filter(category => category !== hoveredName)
        .map(category => ({
          name: category,
          value: dataPoint[category] || 0
        }));

      return (
        <div className="bg-gray-800 border-gray-600 p-4 rounded shadow-lg text-white" style={{ minWidth: '240px' }}>
          <h3 className="text-md font-bold mb-3 uppercase">{tooltipTitle} - {label}</h3>

          {/* Hovered category */}
          <div className="flex items-start mb-2">
            <div className="w-4 h-4 rounded-full mr-3 mt-0.5" style={{ backgroundColor: hoveredColor }} />
            <div className="flex-1 text-sm break-words leading-relaxed pr-2 min-w-0">{hoveredName}</div>
            <div className="flex items-center ml-auto flex-shrink-0">
              <span className="text-sm mr-3">{hoveredValue}</span>
              <span className="text-gray-400 text-sm">{hoveredPercentage} %</span>
            </div>
          </div>

          {/* Other categories */}
          {otherCategories.map((entry) => {
            // Handle NaN percentage when totalValue is 0
            const percentage = totalValue === 0 ? "0.00" : ((entry.value / totalValue) * 100).toFixed(2);
            const entryColor = categoryColorMap[entry.name];
            return (
              <div key={entry.name} className="flex items-start mb-2">
                <div className="w-4 h-4 rounded-full mr-3 mt-0.5" style={{ backgroundColor: entryColor }} />
                <div className="flex-1 text-sm break-words leading-relaxed pr-4 min-w-0">{entry.name}</div>
                <div className="flex items-center ml-auto flex-shrink-0">
                  <span className="text-sm mr-3">{entry.value}</span>
                  <span className="text-gray-400 text-sm">{percentage} %</span>
                </div>
              </div>
            );
          })}

          <div className="border-t border-gray-600 my-2" />

          {/* Total */}
          <div className="flex items-center">
            <div className="w-4 mr-1" />
            <span className="text-sm">Total</span>
            <div className="ml-auto flex items-center">
              <span className="text-sm font-bold mr-3">{totalValue}</span>
              <span className="text-gray-400 text-sm">{totalValue === 0 ? "0" : "100"} %</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const prepareDownloadRows = () => {
    return data.map(item => {
      const row = {};
      row[xAxisKey] = item[xAxisKey];
      categories.forEach(cat => { row[cat] = item[cat] ?? 0; });
      row.total = roundToTwoDecimals(
        categories.reduce((acc, c) => acc + (item[c] ?? 0), 0)
      );
      return row;
    });
  };

  return (
    <div className="bg-white rounded-lg" style={{height: "100%"}}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          {typeof handleDownload === 'function' && (
            <Download className="text-gray-500 hover:text-gray-700 cursor-pointer" size={18} onClick={() => handleDownload(prepareDownloadRows(), title)} />
          )}
        </div>
        
        <div style={{height: "50vh"}}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 10, right: 30, left: 30, bottom: showLegend ? 50 : 30 }}
            barSize={data.length > 15 ? 12 : data.length > 10 ? 20 : 40} // Adjust bar size based on data point count
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEEEE" />}
            <XAxis
              dataKey={xAxisKey}
              scale="point"
              padding={{ left: 30, right: 30, top: 50 }}
              tick={{ fill: axisColor, fontSize: 12 }}
              axisLine={{ stroke: darkMode ? '#374151' : '#E5E7EB', strokeWidth: 1 }}
              tickLine={false}
              interval={data.length > 20 ? 1 : 0}
              angle={data.length > 10 ? -45 : 0}
              textAnchor={data.length > 15 ? 'end' : 'middle'}
              height={data.length > 15 ? 60 : 30}
            >
              <Label
                value={xAxisTitle}
                position="insideBottom"   /* puts it just under the axis line */
                offset={0}                 /* start with zero built-in offset */
                dx={0}                      /* no horizontal shift */
                dy={10}                     /* push it downward by 20px */
                style={{
                  textAnchor: 'middle',    /* center text horizontally */ 
                  fill: axisColor,
                  fontSize: 12,
                  fontWeight: 500
                }}
              />
            </XAxis>
            <YAxis
              tick={{ fill: axisColor, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              tickCount={10}
              label={{ 
                value: yAxisTitle, 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' },
                fill: axisColor,
                fontSize: 12,
                fontWeight: 500,
                dx: -15
              }}
            />
            <Tooltip isAnimationActive={false} content={<CustomTooltip tooltipTitle={title} />} />
            {showLegend && 
              <Legend 
                content={<CustomLegend />}
                verticalAlign="bottom" 
                height={36}
              />
            }
            {categories.map((category, index) => (
              <Bar 
                key={category} 
                dataKey={category} 
                stackId="a" 
                fill={categoryColorMap[category]} 
                radius={[0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
        </div>
    </div>
  );
};

/**
 * Bar (left Y) + line (right Y). Parent passes row array and keys; parent owns filters/dropdowns.
 * Y domains avoid collapsing when all values are zero (same idea as trend charts staying readable).
 */
const DualAxisBarLineChart = ({
  data = [],
  xAxisKey,
  y1Key,
  y2Key,
  y1Name = 'Series 1',
  y2Name = 'Series 2',
  y1AxisLabel,
  y2AxisLabel,
  xAxisLabel,
  y1Color = '#4F46E5',
  y2Color = '#10B981',
  y1Domain: y1DomainProp,
  y2Domain: y2DomainProp,
  y2ShowPercentSuffix = true,
  histogramBars = false,
  barCategoryGap,
  barGap,
  barRadius,
  barSize,
  tooltipLabelFormatter,
  height = '60vh',
  width = 'w-full',
  emptyMessage = 'No data available',
}) => {
  const y1Domain = useMemo(() => {
    if (Array.isArray(y1DomainProp) && y1DomainProp.length === 2) return y1DomainProp;
    const max = Math.max(0, ...data.map((d) => Number(d?.[y1Key]) || 0));
    if (max === 0) return [0, 1];
    return [0, Math.ceil(max * 1.1)];
  }, [data, y1Key, y1DomainProp]);

  const y2Domain = useMemo(() => {
    if (Array.isArray(y2DomainProp) && y2DomainProp.length === 2) return y2DomainProp;
    const max = Math.max(0, ...data.map((d) => Number(d?.[y2Key]) || 0));
    if (max === 0) return [0, 1];
    return [0, Math.ceil(max * 1.1)];
  }, [data, y2Key, y2DomainProp]);

  const leftLabel = y1AxisLabel ?? y1Name;
  const rightLabel = y2AxisLabel ?? y2Name;
  const n = data.length;
  const resolvedBarCategoryGap = barCategoryGap ?? (histogramBars ? 0 : undefined);
  const resolvedBarGap = barGap ?? (histogramBars ? 0 : undefined);
  const resolvedBarRadius = barRadius ?? (histogramBars ? [0, 0, 0, 0] : [6, 6, 0, 0]);
  const resolvedBarSize = barSize ?? (histogramBars ? undefined : n > 24 ? 10 : n > 12 ? 16 : 24);
  const xAxisHeight = n > 18 ? (xAxisLabel ? 88 : 72) : (xAxisLabel ? 52 : 36);

  const CustomTooltip = ({ active, label, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const title = tooltipLabelFormatter ? tooltipLabelFormatter(label, payload) : label;
    const rows = [
      { key: y1Key, name: y1Name, color: y1Color, suffix: '' },
      { key: y2Key, name: y2Name, color: y2Color, suffix: '' },
    ].map((series) => {
      const entry = payload.find((item) => item.dataKey === series.key);
      return {
        ...series,
        value: entry?.value ?? 0,
      };
    });

    return (
      <div
      className=" p-4 rounded shadow-lg" style={{ minWidth: '240px', maxHeight: '60vh', overflow: 'auto',backdropFilter: "blur(15px)", backgroundColor: "rgba(255, 255, 255, 0.1)" }}
      >
        <div className="text-sm font-semibold text-gray-900 mb-3">{title}</div>
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.key} className="flex items-center">
              <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: row.color }} />
              <span className="text-sm font-semibold text-gray-900">{row.name}</span>
              <span className="text-sm font-bold text-gray-900 ml-auto">
                {row.value}{row.suffix}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const CustomLegend = () => (
    <div className="flex items-center justify-center gap-5 mt-2">
      {[
        { name: y1Name, color: y1Color },
        { name: y2Name, color: y2Color },
      ].map((item) => (
        <div key={item.name} className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: item.color }} />
          <span className="text-sm font-medium text-gray-900">{item.name}</span>
        </div>
      ))}
    </div>
  );

  const renderLineDot = (props) => {
    const value = Number(props?.payload?.[y2Key]);
    if (!Number.isFinite(value) || value === 0) return null;
    return (
      <circle
        cx={props.cx}
        cy={props.cy}
        r={2.5}
        fill="#FFFFFF"
        stroke={y2Color}
        strokeWidth={2}
      />
    );
  };

  const renderActiveLineDot = (props) => {
    const value = Number(props?.payload?.[y2Key]);
    if (!Number.isFinite(value) || value === 0) return null;
    return (
      <circle
        cx={props.cx}
        cy={props.cy}
        r={5}
        fill="#FFFFFF"
        stroke={y2Color}
        strokeWidth={2}
      />
    );
  };

  return (
    <div className={width} style={{ height }}>
      {data.length === 0 ? (
        <div className="h-full flex items-center justify-center text-sm text-gray-500">
          {emptyMessage}
        </div>
      ) : (
        <div className="h-full min-h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 16, right: 28, left: 8, bottom: n > 20 ? 56 : 36 }}
              barCategoryGap={resolvedBarCategoryGap}
              barGap={resolvedBarGap}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEEEE" />
              <XAxis
                dataKey={xAxisKey}
                tick={{ fill: '#6B7280', fontSize: 11 }}
                axisLine={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                tickLine={false}
                interval={n > 24 ? 'preserveStartEnd' : 0}
                minTickGap={n > 16 ? 10 : 4}
                angle={n > 18 ? -40 : 0}
                textAnchor={n > 18 ? 'end' : 'middle'}
                height={xAxisHeight}
                label={xAxisLabel ? {
                  value: xAxisLabel,
                  position: 'insideBottom',
                  offset: 0,
                  fill: '#6B7280',
                  fontSize: 12,
                  fontWeight: 500,
                } : undefined}
              />
              <YAxis
                yAxisId="left"
                domain={y1Domain}
                allowDecimals={false}
                tick={{ fill: '#6B7280', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{
                  value: leftLabel,
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' },
                  fill: '#6B7280',
                  fontSize: 12,
                  fontWeight: 500,
                  dx: -4,
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={y2Domain}
                tick={{ fill: '#6B7280', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}`}
                label={{
                  value: rightLabel,
                  angle: 90,
                  position: 'insideRight',
                  style: { textAnchor: 'middle' },
                  fill: '#6B7280',
                  fontSize: 12,
                  fontWeight: 500,
                  dx: 6,
                }}
              />
              <Tooltip
                isAnimationActive={false}
                content={<CustomTooltip />}
                wrapperStyle={{ pointerEvents: 'none' }}
              />
              <Legend content={<CustomLegend />} />
              <Bar
                yAxisId="left"
                dataKey={y1Key}
                name={y1Name}
                fill={y1Color}
                radius={resolvedBarRadius}
                barSize={resolvedBarSize}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey={y2Key}
                name={y2Name}
                stroke={y2Color}
                strokeWidth={2.5}
                dot={renderLineDot}
                activeDot={renderActiveLineDot}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

function SingleRowSegmentedChart({data}) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const formattedTime = (time) => {
    if(!time) return undefined;
    const totalSeconds = parseInt(time);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      // Format as HH:MM:SS
      return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    } else {
      // Format as MM:SS
      return `${String(minutes)}m ${String(seconds)}s`;
    }
  }
  
  return (
    <div className="w-full p-1 bg-white">
      
      {/* Single row segmented bar */}
      <div className="flex w-full h-2 rounded-md overflow-hidden mb-2">
        {data.map((segment, index) => (
          <div
            key={segment.name}
            className="flex items-center justify-center text-white font-semibold transition-all hover:opacity-80"
            style={{
              width: `${(segment.value / total) * 100}%`,
              backgroundColor: segment.color
            }}
          >
          </div>
        ))}
      </div>
      
      {/* Legend */}
      <div className="grid grid-cols-3 justify-center ml-2">
        {data.map((item) => (
          <div key={item.name} className="flex items-baseline gap-2">
            <div
              className="w-2 h-2 rounded"
              style={{ backgroundColor: item.color }}
            ></div>
            <span className="text-xs font-medium flex items-center">
              {item.name} - {Math.round((item.value / total) * 100, 0)}% <br/> ({formattedTime(item.value)})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


export {PerformerCard, TrendsLineWithMetrics, PieChart2, VerticalBarGraph, HorizontalBarMetrics, CompetencyAssessment, TreeMap, DynamicSankeyChart, MetricTabs, NumberCard, StackedBarChart, SingleRowSegmentedChart, DualAxisBarLineChart}
